import React, { useMemo } from 'react'
import { Modal } from 'antd'
import moment from 'moment'
import { DERATE_TYPES, OUTAGE_TYPES } from './drlUtils'

const fmt = v =>
  v != null && v !== '' && !isNaN(parseFloat(v)) ? Number(v).toFixed(1) : '—'

const fmtMin = v => (v != null ? `${v} min` : '—')

const mm = s => String(s).padStart(2, '0')

// Re-derive outage-aware per-segment breakdown for a single hour.
// Mirrors the logic in recalcDerivedRows (drlUtils.js).
const buildSegments = (eventRows, hour) => {
  // Step 1: compute outage intervals for this hour
  const allHourEvents = []
  eventRows.forEach(er => {
    const arr = er.eventDataMap && er.eventDataMap[hour]
    if (!Array.isArray(arr) || !arr.length) return
    const isOutage = OUTAGE_TYPES.has(er.eventData?.eventType)
    arr.forEach(e =>
      allHourEvents.push({ min: moment(e.eventStart).minute(), isOutage })
    )
  })
  allHourEvents.sort((a, b) =>
    a.min !== b.min ? a.min - b.min : a.isOutage ? 1 : -1
  )

  const outageSegs = []
  let outageStart = null
  for (const ev of allHourEvents) {
    if (ev.isOutage && outageStart == null) {
      outageStart = ev.min
    } else if (!ev.isOutage && outageStart != null) {
      outageSegs.push([outageStart, ev.min])
      outageStart = null
    }
  }
  if (outageStart != null) outageSegs.push([outageStart, 60])

  const nonOutageDur = (s, e) => {
    let free = e - s
    for (const [os, oe] of outageSegs) {
      const lo = Math.max(s, os)
      const hi = Math.min(e, oe)
      if (hi > lo) free -= hi - lo
    }
    return Math.max(0, free)
  }

  // Step 2: expand each event group into per-sub-event segments
  const segments = []
  eventRows.forEach(er => {
    const arr = er.eventDataMap && er.eventDataMap[hour]
    if (!Array.isArray(arr) || !arr.length) return
    const isDerate = DERATE_TYPES.has(er.eventData?.eventType)
    const evType = er.eventData?.eventType || ''
    const sorted = arr
      .slice()
      .sort(
        (a, b) => moment(a.eventStart).minute() - moment(b.eventStart).minute()
      )

    sorted.forEach((e, i) => {
      const minStart = moment(e.eventStart).minute()
      const minEnd =
        i < sorted.length - 1 ? moment(sorted[i + 1].eventStart).minute() : 60
      const cap = e.availCap != null ? parseFloat(e.availCap) : 0
      const effMin = isDerate
        ? nonOutageDur(minStart, minEnd)
        : minEnd - minStart
      const effective = cap * (effMin / 60)
      segments.push({
        label: `${evType} :${mm(minStart)}–:${mm(minEnd)}`,
        cap,
        effMin,
        effective,
      })
    })
  })

  return segments
}

const UnallocatedModal = ({ visible, onClose, hour, rowData }) => {
  const breakdown = useMemo(() => {
    if (!hour || !rowData) return null

    const key = `h${hour}`
    const ndcRow = rowData.find(r => r.metric === 'NDC')
    const netGenRow = rowData.find(r => r.metric === 'Net Generation')
    const drlRow = rowData.find(r => r.metric === 'DRL')
    const unallocRow = rowData.find(r => r.metric === 'Unallocated')
    const eventRows = rowData.filter(r => r.rowType === 'event')

    const ndc = ndcRow ? parseFloat(ndcRow[key]) : null
    const netGen = netGenRow ? parseFloat(netGenRow[key]) : null
    const drl = drlRow ? parseFloat(drlRow[key]) : null
    const unalloc = unallocRow ? parseFloat(unallocRow[key]) : null

    const segments = buildSegments(eventRows, hour)
    const sumCap = segments.reduce((s, e) => s + e.cap, 0)
    const sumEffective = segments.reduce((s, e) => s + e.effective, 0)

    return { ndc, netGen, drl, unalloc, segments, sumCap, sumEffective }
  }, [hour, rowData])

  const title = hour
    ? `Unallocated — H${String(hour).padStart(2, '0')} Calculation`
    : 'Unallocated'

  const cellL = (extra = {}) => ({
    padding: '5px 8px',
    fontSize: 13,
    color: '#000000',
    ...extra,
  })
  const cellR = (extra = {}) => ({
    padding: '5px 12px',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    fontSize: 13,
    color: '#000000',
    whiteSpace: 'nowrap',
    ...extra,
  })
  const colHdr = {
    padding: '3px 12px',
    textAlign: 'right',
    fontSize: 11,
    color: '#333333',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  }

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
    >
      {breakdown && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={cellL()}>NDC</td>
              <td colSpan={3} style={cellR()}>
                {fmt(breakdown.ndc)}
              </td>
            </tr>

            <tr>
              <td style={cellL()}>
                <span style={{ color: '#f5222d', marginRight: 4 }}>−</span>DRL
              </td>
              <td colSpan={3} style={cellR()}>
                {fmt(breakdown.drl)}
              </td>
            </tr>

            <tr>
              <td style={cellL()}>
                <span style={{ color: '#f5222d', marginRight: 4 }}>−</span>Net
                Generation
              </td>
              <td colSpan={3} style={cellR()}>
                {fmt(breakdown.netGen)}
              </td>
            </tr>

            {breakdown.segments.length > 0 && (
              <>
                <tr>
                  <td />
                  <td style={colHdr}>Eff Min</td>
                  <td style={colHdr}>Cap</td>
                  <td style={colHdr}>Effective</td>
                </tr>
                <tr>
                  <td style={cellL()}>
                    <span style={{ color: '#f5222d', marginRight: 4 }}>−</span>
                    Events
                  </td>
                  <td style={cellR({ color: '#333333' })}>—</td>
                  <td style={cellR({ color: '#333333' })}>
                    {fmt(breakdown.sumCap)}
                  </td>
                  <td style={cellR()}>{fmt(breakdown.sumEffective)}</td>
                </tr>
                {breakdown.segments.map((seg, i) => (
                  <tr key={i}>
                    <td style={cellL({ paddingLeft: 24 })}>{seg.label}</td>
                    <td style={cellR({ color: '#333333' })}>
                      {fmtMin(seg.effMin)}
                    </td>
                    <td style={cellR({ color: '#333333' })}>{fmt(seg.cap)}</td>
                    <td style={cellR()}>{fmt(seg.effective)}</td>
                  </tr>
                ))}
              </>
            )}

            <tr>
              <td
                colSpan={4}
                style={{ borderTop: '2px solid #d9d9d9', padding: '4px 0' }}
              />
            </tr>
            <tr>
              <td style={cellL({ fontWeight: 700, color: '#000000' })}>
                Unallocated
              </td>
              <td
                colSpan={3}
                style={cellR({
                  fontWeight: 700,
                  color:
                    breakdown.unalloc != null && breakdown.unalloc < 0
                      ? '#f5222d'
                      : '#52c41a',
                })}
              >
                {fmt(breakdown.unalloc)}
              </td>
            </tr>
            <tr>
              <td
                colSpan={4}
                style={{
                  padding: '6px 8px',
                  fontSize: 11,
                  color: '#555',
                  fontStyle: 'italic',
                }}
              >
                {fmt(breakdown.ndc)} − {fmt(breakdown.drl)} −{' '}
                {fmt(breakdown.netGen)} − {fmt(breakdown.sumEffective)} ={' '}
                {fmt(breakdown.unalloc)}
              </td>
            </tr>
          </tbody>
        </table>
      )}
      {!breakdown && (
        <div style={{ color: '#888', fontSize: 13 }}>
          No data for this hour.
        </div>
      )}
    </Modal>
  )
}

export default UnallocatedModal
